import { map } from 'lodash';
import { FunctionComponent, SetStateAction, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Button, Menu } from '@appd/uik';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface Props {
  className: string;
  cols: any;
  rowHeight: number;
  containerPadding: [number, number];
  margin: [number, number];
  children: any[];
  breakpoints: any;
}

export const RGLResponsiveGridLayout: FunctionComponent<Props> = props => {
  const [items, setItems] = useState(props.children);

  const initialLayouts = {
    lg: map(props.children, function (child) {
      let childProps = child.props.gridItem;
      return {
        i: child.key,
        static: childProps?.static ? childProps.static : false,
        isResizable: childProps?.isResizable,
        x: childProps.x,
        y: childProps.y,
        w: childProps.w,
        h: childProps.h,
      };
    }),
  };

  const initialViewMenuStatus = props.children.reduce((acc, child, i) => {
    acc[i] = child.props.gridItem?.viewPanel;
    return acc;
  }, {} as { [index: number]: boolean });

  const initialCollapseStatus = props.children.reduce((acc, child, i) => {
    const childProps = child.props.gridItem;
    acc[i] = childProps?.collapsible && !childProps?.isResizable && childProps.h == 1;
    return acc;
  }, {} as { [index: number]: boolean });

  const [viewMenuStatus, setViewMenuStatus] = useState<{
    [index: number]: boolean;
  }>(initialViewMenuStatus);

  const [collapseButtonStatus, setCollapseButtonStatus] = useState<{
    [index: number]: boolean;
  }>(initialCollapseStatus);

  const [layouts, setLayouts] = useState<{ [index: string]: any[] }>(initialLayouts);

  const [prevLayouts, setPrevLayouts] = useState<{ [index: string]: any[] }>(initialLayouts);

  const [rowStatus, setRowStatus] = useState<{ [index: number]: boolean }>(initialCollapseStatus);

  const [foldedCards, setFoldedCards] = useState<{ [index: number]: any[] }>({});

  const toggleRow = (gridId: number) => {
    setRowStatus(prev => ({ ...prev, [gridId]: !prev[gridId] }));
    const currentRowY = layouts.lg[gridId].y;
    let nextRowY = Infinity;
    layouts.lg.forEach((obj, i) => {
      if (collapseButtonStatus[i] == true && obj.y > currentRowY) {
        nextRowY = Math.min(nextRowY, obj.y);
      }
    });
    if (!rowStatus[gridId]) {
      let folded: any[] = [];
      let newLayouts = {
        lg: map(layouts.lg, function (child) {
          if (child.y > currentRowY && child.y < nextRowY) {
            const childY = child.y;
            folded.push({
              ...child,
              y: childY - currentRowY,
            });
            return {
              ...child,
              h: 0,
              minH: 0,
            };
          } else if (child.y >= nextRowY) {
            const newY = child.y - (nextRowY - currentRowY - 1);
            return {
              ...child,
              y: newY,
            };
          } else return child;
        }),
      };
      setFoldedCards(prev => ({ ...prev, [gridId]: folded }));
      setLayouts(newLayouts);
    } else if (rowStatus[gridId] && foldedCards[gridId] != null) {
      const unfold = foldedCards[gridId];
      let newSpace = 10;
      unfold.forEach(obj => (newSpace = Math.max(newSpace, obj.y + obj.h)));
      let newLayouts = {
        lg: map(layouts.lg, function (child) {
          if (child.h == 0) {
            let found = false;
            for (const folded of unfold) {
              if (folded.i === child.i) {
                return {
                  ...child,
                  y: currentRowY + folded.y,
                  h: folded.h,
                  minH: folded.minH,
                };
              }
            }
            if (!found) return child;
          } else if (child.y > currentRowY) {
            let oldY = child.y;
            return {
              ...child,
              y: oldY + newSpace,
            };
          } else return child;
        }),
      };
      setFoldedCards(prev => {
        delete prev[gridId];
        return prev;
      });
      setLayouts(newLayouts);
    }
  };

  const toggleFullScreen = (gridId: number) => {
    setPrevLayouts(layouts);
    const newLayouts = {
      lg: map(layouts.lg, function (child, i) {
        if (i === gridId)
          return {
            i: i.toString(),
            ...child,
            x: 0,
            y: 0,
            w: props.cols.lg,
            h: window.innerHeight / props.rowHeight,
          };
        else
          return {
            i: i.toString(),
            ...child,
            w: 0,
            h: 0,
          };
      }),
    };
    setLayouts(newLayouts);
  };

  const onLayoutChange = (_layout: unknown, layouts: SetStateAction<{ [index: string]: unknown[] }>) => {
    setLayouts(layouts);
  };

  const createMenuTrigger = <Button icon="Navigation.MoreHorizontal" type="MENU"></Button>;

  const { Option } = Menu;

  return (
    <div>
      <ResponsiveGridLayout
        className={props.className}
        layouts={layouts}
        cols={props.cols}
        breakpoints={props.breakpoints}
        rowHeight={props.rowHeight}
        containerPadding={props.containerPadding}
        margin={props.margin}
        onLayoutChange={onLayoutChange}
      >
        {items}
      </ResponsiveGridLayout>
    </div>
  );
};
